import { useNextEnvVars } from "@/components/context-providers/NextEnvVarsWrapper";
import { parseCookie } from "@/utils/parse-cookie";
import {
  Identify,
  identify,
  init,
  reset,
  setGroup,
  setOptOut,
  setUserId,
  track,
} from "@amplitude/analytics-browser";
import { LogLevel, type ValidPropertyType } from "@amplitude/analytics-types";
import { useSession } from "next-auth/react";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { useRouteAndTeamInfo } from "@/utils/hooks/url/use-route-info";
import { useDANGEROUSisChalkEmployee } from "@/utils/hooks/use-is-chalk-employee";
import { useIsImpersonating } from "@/utils/hooks/use-is-impersonating";

export const amplitudeTrack = (
  amplitudeKey: string | null,
  ...args: Parameters<typeof track>
): ReturnType<typeof track> | undefined => {
  if (!amplitudeKey) {
    return;
  }
  return track(...args);
};

export const amplitudeIdentify = (
  amplitudeKey: string | null,
  properties: Record<string, ValidPropertyType | null | undefined>,
) => {
  if (!amplitudeKey) {
    return;
  }
  const identifyClass = new Identify();
  const propertyEntries = Object.entries(properties);
  for (const [key, val] of propertyEntries) {
    if (val != null) {
      identifyClass.set(key, val);
    }
  }

  return identify(identifyClass);
};

/**
 * The Amplitude API key, or null when events must not be sent: no key is configured, or the
 * user is (or may be) impersonating another team. Pass the result to `amplitudeTrack` and
 * `amplitudeIdentify`, which drop events for a null key.
 */
export const useAmplitudeKey = (): string | null => {
  const { NEXT_PUBLIC_AMPLITUDE_API_KEY } = useNextEnvVars();
  const isImpersonating = useIsImpersonating();
  return isImpersonating === false ? NEXT_PUBLIC_AMPLITUDE_API_KEY : null;
};

/** Forgets the current user and device, so later events are not attributed to them. */
export const amplitudeReset = () => {
  reset();
};

export const useAmplitudeBaseIdentify = () => {
  const session = useSession();
  const { environmentId, projectId, team, environment, isServerlessEnvironment } =
    useRouteAndTeamInfo();
  const isChalkEmployee = useDANGEROUSisChalkEmployee({ ignoreAdminMode: true });
  const user = session.data?.user;
  // The viewed team, falling back to the session's team before GetTeam resolves. The two
  // only differ while impersonating, which `useAmplitudeKey` excludes from tracking.
  const teamId = team?.id ?? session.data?.teamId ?? null;
  const isServerless = environment != null ? isServerlessEnvironment ?? false : null;
  const { CHALK_DEPLOYMENT_MODEL, CHALK_PLATFORM_VERSION } = useNextEnvVars();
  const NEXT_PUBLIC_AMPLITUDE_API_KEY = useAmplitudeKey();

  useEffect(() => {
    if (NEXT_PUBLIC_AMPLITUDE_API_KEY != null && teamId != null) {
      setGroup("team", teamId);
    }
    amplitudeIdentify(NEXT_PUBLIC_AMPLITUDE_API_KEY, {
      team_id: teamId,
      email: user?.email,
      is_chalk_employee: isChalkEmployee,
      active_environment: environmentId,
      active_project: projectId,
      team_name: team?.name,
      active_environment_name: environment?.name,
      deployment_model: CHALK_DEPLOYMENT_MODEL,
      platform_version: CHALK_PLATFORM_VERSION,
      is_serverless: isServerless,
    });
  }, [
    CHALK_DEPLOYMENT_MODEL,
    CHALK_PLATFORM_VERSION,
    NEXT_PUBLIC_AMPLITUDE_API_KEY,
    environment?.name,
    environmentId,
    isChalkEmployee,
    isServerless,
    projectId,
    team?.name,
    teamId,
    user?.email,
  ]);
};

export const useAmplitudePageview = (eventName: string) => {
  const session = useSession();
  const router = useRouter();
  const { NEXT_PUBLIC_AMPLITUDE_API_KEY } = useNextEnvVars();
  const isImpersonating = useIsImpersonating();
  const userId = session.data?.userId ?? null;
  const routerAsPathWithoutQueryOrHash = router.asPath.split(/[?#]/)[0];
  useEffect(() => {
    const initAndLog = async () => {
      if (parseCookie(document.cookie)["ddog"] === "1") {
        console.log("Skipping ampl instrumentation; ddog synthetic");
        return;
      }
      if (!NEXT_PUBLIC_AMPLITUDE_API_KEY || isImpersonating == null) {
        return;
      }
      // Opt-out persists in Amplitude's cookie, so it is set explicitly on every page. Calls
      // made before `init` resolves are queued in order, so this lands ahead of any identify
      // or track queued by sibling hooks in the same commit.
      setOptOut(isImpersonating);
      await init(NEXT_PUBLIC_AMPLITUDE_API_KEY, undefined, {
        serverUrl: "/api/am",
        defaultTracking: false,
        optOut: isImpersonating,
        // The SDK's default logger writes delivery failures (ad blockers,
        // flaky networks) to console.error, which Datadog RUM ingests as
        // frontend errors. Analytics delivery is best-effort; silence it.
        logLevel: LogLevel.None,
        cookieOptions: {
          secure: true,
        },
      }).promise;
      if (isImpersonating) {
        return;
      }
      if (userId != null) {
        setUserId(userId);
      }
      amplitudeTrack(NEXT_PUBLIC_AMPLITUDE_API_KEY, eventName, {
        path: routerAsPathWithoutQueryOrHash,
        routerPath: router.pathname,
      });
    };

    initAndLog().catch((err) => console.error(err));
  }, [
    NEXT_PUBLIC_AMPLITUDE_API_KEY,
    eventName,
    isImpersonating,
    routerAsPathWithoutQueryOrHash,
    router.pathname,
    userId,
  ]);
};
